import type { ProductFormValues, ProductVariantFormValues } from './schemas'
import { ProductType } from './types'
import type { ProductDetail, ProductOption, SaveProductBody } from './types'

/** `S, M, L` — one line in, the axis values out, blanks and duplicates dropped. */
export function parseOptionValues(input: string): string[] {
  const values = input
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean)

  return [...new Set(values)]
}

/** The names of the axes a variant must carry a value for. */
export function optionAxisNames(options: ProductFormValues['options']): string[] {
  return options.map((option) => option.name.trim()).filter(Boolean)
}

/**
 * The ceiling on a generated matrix.
 *
 * Three axes of five values is 125 rows, each one a handful of registered fields —
 * past this the dialog stops being editable long before the merchant stops adding
 * values. The cap is reported rather than silently applied: the section says how
 * many combinations were left out.
 */
export const MAX_GENERATED_VARIANTS = 100

/**
 * Joins a row's values into one comparable key.
 *
 * A NUL rather than a dash or a tab: the key is compared against values the
 * merchant typed, and any character they can type is one that could appear
 * inside a value and split the key in the wrong place.
 */
const VALUE_SEPARATOR = '\u0000'

/** `[[S, Red], [S, Blue], [M, Red], ...]` — the axes crossed, in the order declared. */
function crossProduct(valueLists: string[][]): string[][] {
  return valueLists.reduce<string[][]>(
    (rows, values) => rows.flatMap((row) => values.map((value) => [...row, value])),
    [[]],
  )
}

/** The values of each complete axis — an axis with no name or no values is not one. */
function optionValueLists(options: ProductFormValues['options']): string[][] {
  return options
    .filter((option) => option.name.trim())
    .map((option) => parseOptionValues(option.values))
    .filter((values) => values.length > 0)
}

/** How many rows a full matrix of these options would be. Zero when there are none. */
export function optionCombinationCount(options: ProductFormValues['options']): number {
  const lists = optionValueLists(options)
  return lists.length === 0 ? 0 : lists.reduce((total, values) => total * values.length, 1)
}

/**
 * Turns "Size: S, M, L" into the rows that sell it.
 *
 * A variant the merchant has already filled in is carried across whole when its
 * values still name a combination — that is what identifies a row, so the SKU, the
 * price, the stock and the child's id all come with it.
 *
 * Adding a second axis renames every combination, so nothing matches whole. Rather
 * than resetting a priced catalogue to the parent's price, each new row takes the
 * price of the row it was split out of: `M` at $18 becomes `M/Red` and `M/Blue` at
 * $18. Only the money — price, cost and incentive travel together, since splitting
 * by colour changes none of them — while the SKU and the id have to be new, because
 * these are new children. Combinations that no longer exist are dropped: a row
 * pinned to a value that has been removed is a row the server rejects.
 */
export function variantsForOptions(
  options: ProductFormValues['options'],
  existing: ProductVariantFormValues[],
  fallback: { sku?: string; price?: number; cost?: number; incentive?: number },
): ProductVariantFormValues[] {
  const axes = options.filter((option) => option.name.trim()).map((option) => option.name.trim())
  const lists = optionValueLists(options)

  // An axis that is named but has no values yet would silently drop out of the
  // cross product, giving rows the server then rejects for a missing attribute.
  if (lists.length === 0 || lists.length !== axes.length) return []

  const keyOf = (values: string[]) => values.join(VALUE_SEPARATOR)
  const byKey = new Map(
    existing.map((variant) => [keyOf(variant.attributes.map((item) => item.value)), variant]),
  )

  /** The row this combination was split out of, longest match first. */
  const splitFrom = (values: string[]) => {
    for (let length = values.length - 1; length > 0; length -= 1) {
      const hit = byKey.get(keyOf(values.slice(0, length)))
      if (hit) return hit
    }
    return undefined
  }

  return crossProduct(lists)
    .slice(0, MAX_GENERATED_VARIANTS)
    .map((values, index) => {
      const attributes = values.map((value, axisIndex) => ({ name: axes[axisIndex], value }))
      const kept = byKey.get(keyOf(values))

      if (kept) return { ...kept, attributes }

      const source = splitFrom(values)

      return {
        sku: defaultVariantSku(fallback.sku, values, index),
        gtin: '',
        // The parent's price rather than zero: free is a real answer, and one the
        // merchant should have to mean rather than inherit from an empty field.
        price: source?.price ?? fallback.price ?? 0,
        // Taken from the same row as the price, so a split row never pairs one
        // row's price with another's cost. With no row to split from, the parent's
        // — and with none there either, blank, never zero: see `costSchema`.
        cost: source ? source.cost : fallback.cost,
        incentive: source ? source.incentive : fallback.incentive,
        thumbnail: null,
        attributes,
      }
    })
}

/** `TSHIRT` + `Medium` -> `TSHIRT-MEDIUM`. Never under the two characters the API wants. */
function defaultVariantSku(base: string | undefined, values: string[], index: number): string {
  const sku = [base?.trim(), ...values]
    .filter(Boolean)
    .join('-')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-|-$/g, '')

  return sku.length >= 2 ? sku : `${sku || 'VARIANT'}-${index + 1}`
}

/**
 * `Margin 58%` — what a sale keeps once the unit cost is paid, as a share of the
 * price. Nothing unless both figures are in, and nothing on a free item, where
 * there is no price for the cost to be a share of.
 */
export function marginLabel(price?: number, cost?: number): string | undefined {
  if (price === undefined || cost === undefined) return undefined
  if (Number.isNaN(price) || Number.isNaN(cost) || price <= 0) return undefined

  return `Margin ${Math.round(((price - cost) / price) * 100)}%`
}

export const emptyProductForm: ProductFormValues = {
  thumbnail: null,
  bannerImages: [],
  nameEn: '',
  nameKm: '',
  descriptionEn: '',
  descriptionKm: '',
  sku: '',
  gtin: '',
  type: ProductType.Stockable,
  unit: undefined,
  price: 0,
  // Blank, not zero — a cost of zero is a claim, and the margin report would take it.
  cost: undefined,
  incentive: undefined,
  published: true,
  categoryIds: [],
  brandId: null,
  tenantBranchIds: [],
  options: [],
  variants: [],
  choiceGroups: [],
}

/**
 * The ids of the branches a loaded product is carried at.
 *
 * Reads whichever of `tenantBranches`, `branches` or `tenantBranchIds` the detail
 * response used, and each entry as an object or a bare id — see `ProductDetail`.
 * Blanks and repeats are dropped so the picker never holds an id it cannot show.
 */
export function branchIdsOf(product: ProductDetail): string[] {
  const refs = product.tenantBranches ?? product.branches ?? product.tenantBranchIds ?? []

  const ids = refs
    .map((ref) => (typeof ref === 'string' ? ref : (ref.id ?? ref.tenantBranchId)))
    .filter((id): id is string => Boolean(id))

  return [...new Set(ids)]
}

/** The brand a loaded product was saved with, from the embedded brand or the bare id. */
export function brandIdOf(product: ProductDetail | null | undefined): string | null {
  return product?.brand?.id ?? product?.brandId ?? null
}

/**
 * What the save body says about the brand.
 *
 * `null` only when the merchant cleared a brand the product was saved with —
 * that is the one case where the server has to be told to take it off. A
 * product that never had one leaves the key out, so a save from a form that
 * never touched the field cannot read as "remove the brand".
 */
export function brandIdForSave(
  brandId: string | null,
  savedBrandId: string | null,
): string | null | undefined {
  if (brandId) return brandId
  return savedBrandId ? null : undefined
}

/**
 * A loaded product, in the shape the form edits.
 *
 * Variants come back as list rows on `children`, which is the only place they appear
 * — the detail payload has no other record of them. Dropping them here would drop
 * them from the save body too, and the server deletes the children a save omits.
 */
export function toFormValues(product: ProductDetail): ProductFormValues {
  const options = (product.options ?? []).map((option: ProductOption) => ({
    name: option.name ?? '',
    values: (option.values ?? []).map((value) => value.name).join(', '),
  }))

  const axes = optionAxisNames(options)

  return {
    id: product.id,
    thumbnail: product.thumbnail ?? null,
    bannerImages: product.images ?? [],
    nameEn: product.name?.en ?? '',
    nameKm: product.name?.km ?? '',
    descriptionEn: product.description?.en ?? '',
    descriptionKm: product.description?.km ?? '',
    sku: product.sku ?? '',
    gtin: product.gtin ?? '',
    type: (product.type as ProductFormValues['type']) ?? ProductType.Stockable,
    unit: (product.unit as ProductFormValues['unit']) ?? undefined,
    price: product.price ?? 0,
    // A server a release behind leaves these out; missing reads as not set.
    cost: product.cost ?? undefined,
    incentive: product.incentive ?? undefined,
    published: Boolean(product.published),
    categoryIds: (product.categories ?? []).map((category) => category.id),
    brandId: brandIdOf(product),
    tenantBranchIds: branchIdsOf(product),
    options,
    variants: (product.children ?? []).map((child) => ({
      id: child.id,
      sku: child.sku ?? '',
      gtin: child.gtin ?? '',
      price: child.price ?? 0,
      cost: child.cost ?? undefined,
      incentive: child.incentive ?? undefined,
      thumbnail: child.thumbnail ?? null,
      // A variant saved before an axis existed has no value for it; the row still
      // renders one input per axis so the gap is visible rather than silent.
      attributes: axes.map((name) => ({
        name,
        value:
          (child.productAttributes ?? []).find((attribute) => attribute.name === name)?.value ?? '',
      })),
    })),
    choiceGroups: (product.choices ?? []).map((choice) => ({
      choiceGroupId: choice.id,
      minSelect: choice.minSelect ?? 1,
      maxSelect: choice.maxSelect ?? 1,
    })),
  }
}

/** Blank means "not set", and the API would rather have the field missing than empty. */
function optional(value: string | undefined): string | undefined {
  const trimmed = value?.trim()
  return trimmed ? trimmed : undefined
}

/**
 * The form's values as the save body.
 *
 * The thumbnail travels as an id — the file itself was uploaded the moment it was
 * picked; the gallery travels the same way, as `bannerImageIds`. `specImageIds` goes up
 * empty — this screen attaches no spec shots — but is still sent because the server
 * reads all four id arrays without a guard. `tenantBranchIds` is the merchant's own
 * pick from the form.
 *
 * `savedBrandId` is the brand the product had when the form opened — `null` for a
 * new product — which is what tells a cleared field from one that was never set.
 */
export function toSaveBody(
  values: ProductFormValues,
  savedBrandId: string | null = null,
): SaveProductBody {
  const options: ProductOption[] = values.options
    .filter((option) => option.name.trim())
    .map((option) => ({
      name: option.name.trim(),
      values: parseOptionValues(option.values).map((name) => ({ name })),
    }))

  return {
    id: values.id,
    // Omitted, not nulled, when there is no picture — see `SaveProductBody`.
    thumbnailId: values.thumbnail?.id,
    name: { en: values.nameEn.trim(), km: optional(values.nameKm) ?? values.nameEn.trim() },
    description: {
      en: optional(values.descriptionEn) ?? '',
      km: optional(values.descriptionKm) ?? '',
    },
    sku: optional(values.sku),
    gtin: optional(values.gtin),
    unit: values.unit,
    type: values.type,
    price: values.price,
    // Always sent, unlike the brand: the form holds the whole figure, so an empty
    // box is the merchant clearing it, and `null` is how the server is told.
    cost: values.cost ?? null,
    incentive: values.incentive ?? null,
    published: values.published,
    categoryIds: values.categoryIds,
    brandId: brandIdForSave(values.brandId, savedBrandId),
    specImageIds: [],
    bannerImageIds: values.bannerImages.map((image) => image.id),
    tenantBranchIds: values.tenantBranchIds,
    options,
    choiceGroups: values.choiceGroups.map((group) => ({
      choiceGroupId: group.choiceGroupId,
      minSelect: group.minSelect,
      maxSelect: group.maxSelect,
    })),
    productVariants: values.variants.map((variant) => ({
      id: variant.id,
      sku: variant.sku.trim(),
      gtin: optional(variant.gtin),
      price: variant.price,
      cost: variant.cost ?? null,
      incentive: variant.incentive ?? null,
      // Omitted, not nulled, when there is no picture — same rule as the product's own.
      thumbnailId: variant.thumbnail?.id,
      // Only on the way in. The server runs the row through `omitBy(isNil)`, so
      // leaving this out of an existing variant's body keeps whatever inventory
      // has since counted; a brand-new child has no count yet and the column has
      // no default, so it would otherwise be created null rather than empty.
      stockQuantity: variant.id ? undefined : 0,
      productAttributes: variant.attributes.map((attribute) => ({
        name: attribute.name,
        value: attribute.value.trim(),
      })),
    })),
  }
}
